/**
 * Podcast service for API interactions
 * Based on reportService but customized for podcasts and episodes
 */
import apiClient from './api';

class PodcastService {
  constructor() {
    this.baseURL = '/podcasts';
  }

  // Podcast management methods
  async getPodcasts(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `${this.baseURL}?${queryString}` : this.baseURL;
      const response = await apiClient.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching podcasts:', error);
      throw error;
    }
  }

  async getPodcast(podcastId) {
    try {
      const response = await apiClient.get(`${this.baseURL}/${podcastId}`);
      return response;
    } catch (error) {
      console.error('Error fetching podcast:', error);
      throw error;
    }
  }

  async createPodcast(podcastData) {
    try {
      const response = await apiClient.post(this.baseURL, podcastData);
      return response;
    } catch (error) {
      console.error('Error creating podcast:', error);
      throw error;
    }
  }

  async updatePodcast(podcastId, updateData) {
    try {
      const response = await apiClient.put(`${this.baseURL}/${podcastId}`, updateData);
      return response;
    } catch (error) {
      console.error('Error updating podcast:', error);
      throw error;
    }
  }

  async deletePodcast(podcastId) {
    try {
      const response = await apiClient.delete(`${this.baseURL}/${podcastId}`);
      return response;
    } catch (error) {
      console.error('Error deleting podcast:', error);
      throw error;
    }
  }

  async refreshEpisodes(podcastId, forceRefresh = false) {
    try {
      const params = forceRefresh ? '?force_refresh=true' : '';
      const response = await apiClient.post(`${this.baseURL}/${podcastId}/refresh-episodes${params}`, {}, {
        timeout: 60000  // 1 minute timeout for RSS parsing
      });
      return response;
    } catch (error) {
      console.error('Error refreshing episodes:', error);
      throw error;
    }
  }

  // Episode management methods
  async getEpisodes(podcastId, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = queryString 
        ? `${this.baseURL}/${podcastId}/episodes?${queryString}` 
        : `${this.baseURL}/${podcastId}/episodes`;
      const response = await apiClient.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching episodes:', error);
      throw error;
    }
  }

  async getEpisode(episodeId) {
    try {
      const response = await apiClient.get(`${this.baseURL}/episodes/${episodeId}`);
      return response;
    } catch (error) {
      console.error('Error fetching episode:', error);
      throw error;
    }
  }

  async updateEpisode(episodeId, updateData) {
    try {
      const response = await apiClient.put(`${this.baseURL}/episodes/${episodeId}`, updateData);
      return response;
    } catch (error) {
      console.error('Error updating episode:', error);
      throw error;
    }
  }

  async deleteEpisode(episodeId) {
    try {
      const response = await apiClient.delete(`${this.baseURL}/episodes/${episodeId}`);
      return response;
    } catch (error) {
      console.error('Error deleting episode:', error);
      throw error;
    }
  }

  async transcribeEpisode(episodeId, forceRetranscribe = false) {
    try {
      const params = forceRetranscribe ? '?force_retranscribe=true' : '';
      const response = await apiClient.post(`${this.baseURL}/episodes/${episodeId}/transcribe${params}`, {}, {
        timeout: 300000  // 5 minutes timeout for audio transcription
      });
      return response;
    } catch (error) {
      console.error('Error transcribing episode:', error);
      throw error;
    }
  }

  // TODO: Implement episode article generation (future feature)
  async generateArticleFromEpisode(episodeId) {
    try {
      // This will need to be implemented on the backend
      const response = await apiClient.post(`${this.baseURL}/episodes/${episodeId}/generate-article`, {}, {
        timeout: 120000  // 2 minutes timeout for article generation
      });
      return response;
    } catch (error) {
      console.error('Error generating article from episode:', error);
      throw error;
    }
  }

  // Statistics
  async getStats() {
    try {
      const response = await apiClient.get(`${this.baseURL}/stats/overview`);
      return response;
    } catch (error) {
      console.error('Error fetching podcast stats:', error);
      throw error;
    }
  }

  // Utility methods
  formatDuration(seconds) {
    if (!seconds) return 'Unknown';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
  }

  formatFileSize(bytes) {
    if (!bytes) return 'Unknown';
    
    const sizes = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let size = bytes;
    
    while (size >= 1024 && i < sizes.length - 1) {
      size /= 1024;
      i++;
    }
    
    return `${size.toFixed(1)} ${sizes[i]}`;
  }

  validateRSSUrl(url) {
    if (!url) return false;
    
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  }
}

// Export singleton instance
const podcastService = new PodcastService();
export default podcastService;